import React, { useState } from 'react';
import { initializeNomenclatures } from '../../../scripts/initialize-nomenclatures';
import Modal from '../../ui/Modal';
import { useNotifications } from '../../NotificationSystem';
import './InitializeButton.css';

/**
 * Initialize Button Component
 * Populates all nomenclatures with default data.
 * Optional labels for use in IPC (ProcessCategoryManager): buttonLabel, initializingLabel, modalTitle, modalMessage, cancelLabel, continueLabel
 */
const InitializeButton = ({ onComplete, buttonLabel = 'Initialize Default Data', initializingLabel = 'Initializing...', modalTitle = 'Initialize Default Data', modalMessage = 'This will populate all nomenclatures with default data. Continue?', cancelLabel = 'Cancel', continueLabel = 'Continue' }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { notify } = useNotifications();

  const handleInitializeClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    
    try {
      setLoading(true);
      setError(null);
      
      // Get current user ID (replace with actual auth)
      const userId = 'system-init'; // TODO: Get from auth context
      
      const result = await initializeNomenclatures(userId);
      setResult(result);
      
      // Refresh so Protocol Types tab and Add Protocol form see new data
      if (onComplete) {
        onComplete();
      }
      
      if (result.success !== false) {
        setShowSuccessModal(true);
        notify.success(result.message || 'All nomenclatures initialized successfully!');
      } else {
        notify.info(result.message || 'Initialization cancelled.');
      }
      
    } catch (err) {
      console.error('Error initializing:', err);
      setError(err.message);
      notify.error('Error initializing nomenclatures. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="initialize-button-container">
        <button
          onClick={handleInitializeClick}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? (
            <>
              <span className="spinner-small"></span>
              Initializing...
            </>
          ) : (
            <>
              🚀 Initialize Default Data
            </>
          )}
        </button>
        
        {result && (
          <div className="init-success">
            <p>✅ Successfully initialized:</p>
            <ul>
              {result.counts.protocolCategories != null && <li>Protocol Categories: {result.counts.protocolCategories}</li>}
              {result.counts.protocolTypes != null && <li>Protocol Types: {result.counts.protocolTypes}</li>}
              {result.counts.processCategories != null && <li>Process Categories: {result.counts.processCategories}</li>}
              {result.counts.roomTypes != null && <li>Room Types: {result.counts.roomTypes}</li>}
              {result.counts.ppeTypes != null && <li>PPE Types: {result.counts.ppeTypes}</li>}
              {result.counts.verificationMethods != null && <li>Verification Methods: {result.counts.verificationMethods}</li>}
              {result.counts.triggerEvents != null && <li>Trigger Events: {result.counts.triggerEvents}</li>}
              {result.counts.frequencyTypes != null && <li>Frequency Types: {result.counts.frequencyTypes}</li>}
              {result.counts.locationTypes != null && <li>Location Types: {result.counts.locationTypes}</li>}
            </ul>
          </div>
        )}
        
        {error && (
          <div className="init-error">
            <p>❌ Error: {error}</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={modalTitle}
        size="small"
      >
        <div className="initialize-modal-content">
          <p>{modalMessage}</p>
          <div className="modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              {cancelLabel}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={loading}
            >
              {loading ? initializingLabel : continueLabel}
            </button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Initialization Complete"
        size="medium"
      >
        <div className="initialize-modal-content">
          <p>✅ All nomenclatures initialized successfully!</p>
          {result && (
            <div className="init-success-list">
              <p><strong>Successfully initialized:</strong></p>
              <ul>
                {result.counts.protocolCategories != null && <li>Protocol Categories: {result.counts.protocolCategories}</li>}
                {result.counts.protocolTypes != null && <li>Protocol Types: {result.counts.protocolTypes}</li>}
                {result.counts.processCategories != null && <li>Process Categories: {result.counts.processCategories}</li>}
                {result.counts.roomTypes != null && <li>Room Types: {result.counts.roomTypes}</li>}
                {result.counts.ppeTypes != null && <li>PPE Types: {result.counts.ppeTypes}</li>}
                {result.counts.verificationMethods != null && <li>Verification Methods: {result.counts.verificationMethods}</li>}
                {result.counts.triggerEvents != null && <li>Trigger Events: {result.counts.triggerEvents}</li>}
                {result.counts.frequencyTypes != null && <li>Frequency Types: {result.counts.frequencyTypes}</li>}
                {result.counts.locationTypes != null && <li>Location Types: {result.counts.locationTypes}</li>}
              </ul>
            </div>
          )}
          <div className="modal-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowSuccessModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default InitializeButton;

